import React, { useState, useEffect, useCallback } from 'react';
import { utils } from '../../utils';
import { SystemModule } from '../../types/permissions';
import { usePermissions } from '../../hooks';
import {
  listarNaoConformidades,
  atualizarNaoConformidade,
  obterEstatisticasNaoConformidades,
  NaoConformidade,
  NaoConformidadeEstatisticas
} from '../../services/naoConformidades';
import {
  listarDivergenciaOcorrencias,
  atualizarDivergenciaOcorrencia,
  DivergenciaOcorrencia,
  DivergenciaOcorrenciaEstatisticas,
  obterEstatisticasDivergenciaOcorrencias
} from '../../services/divergenciaOcorrencias';
import { listarTiposOcorrencia } from '../../services/tiposOcorrenciaDivergencia';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import { getHojeISO, toInicioDiaISO, toFimDiaISO } from '../../utils/dateHelpers';
import '../../pages/MontagemPaletes/MontagemPaletes.css';

const severidadeLabel: Record<string, string> = {
  info: 'Info',
  aviso: 'Aviso',
  critico: 'Crítico'
};

const severidadeClass: Record<string, string> = {
  info: 'status-info',
  aviso: 'status-warning',
  critico: 'status-danger'
};

const statusLabel: Record<string, string> = {
  pendente: 'Pendente',
  analisando: 'Analisando',
  resolvido: 'Resolvido'
};

const statusClass: Record<string, string> = {
  pendente: 'status-warning',
  analisando: 'status-info',
  resolvido: 'status-active'
};

const tipoLabel: Record<string, string> = {
  etiqueta_nao_reconhecida: 'Etiqueta não reconhecida',
  palete_nao_identificado: 'Palete não identificado',
  area_nao_encontrada: 'Área não encontrada',
  erro_registro: 'Erro ao registrar',
  etiqueta_sem_pre_carregamento: 'Sem pré-carregamento',
  erro_identificacao_palete: 'Erro identificação palete'
};

// Fallback hardcoded - será substituído pelos dados dinâmicos do backend
const OCORRENCIA_TIPO_LABEL_FALLBACK: Record<string, string> = {
  volume_faltante: 'Volume faltante',
  volume_extraviado: 'Volume extraviado',
  volume_danificado: 'Volume danificado',
  erro_bipagem: 'Erro de bipagem',
  etiqueta_ilegivel: 'Etiqueta ilegível',
  pedido_incompleto: 'Pedido incompleto',
  outro: 'Outro'
};

type TabAtiva = 'nao_conformidades' | 'divergencias';

// Modal de justificativa para resolução
const JustificativaModal: React.FC<{
  isOpen: boolean;
  titulo: string;
  onConfirm: (justificativa: string) => void;
  onCancel: () => void;
}> = ({ isOpen, titulo, onConfirm, onCancel }) => {
  const [justificativa, setJustificativa] = useState('');

  useEffect(() => {
    if (isOpen) setJustificativa('');
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 24,
        width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <h3 style={{ margin: '0 0 4px 0', fontSize: '1.1rem', color: '#2c3e50' }}>
          <i className="fas fa-check-circle" style={{ marginRight: 8, color: '#27ae60' }}></i>
          Marcar como Resolvido
        </h3>
        <p style={{ margin: '0 0 16px 0', fontSize: '0.85rem', color: '#7f8c8d' }}>
          {titulo}
        </p>
        <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#7f8c8d', textTransform: 'uppercase', marginBottom: 6 }}>
          Justificativa da resolução *
        </label>
        <textarea
          value={justificativa}
          onChange={(e) => setJustificativa(e.target.value)}
          placeholder="Descreva como o problema foi resolvido..."
          rows={4}
          style={{
            width: '100%', padding: '10px 12px', border: '1px solid #dce1e6',
            borderRadius: 8, fontSize: '0.9rem', resize: 'vertical',
            fontFamily: 'inherit',
          }}
          autoFocus
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            className="btn btn-secondary"
            onClick={onCancel}
            style={{ padding: '8px 20px' }}
          >
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={() => onConfirm(justificativa.trim())}
            disabled={!justificativa.trim()}
            style={{
              padding: '8px 20px',
              background: justificativa.trim() ? '#27ae60' : '#bdc3c7',
              borderColor: justificativa.trim() ? '#27ae60' : '#bdc3c7',
            }}
          >
            <i className="fas fa-check" style={{ marginRight: 6 }}></i>
            Confirmar Resolução
          </button>
        </div>
      </div>
    </div>
  );
};

const NaoConformidadesList: React.FC = () => {
  const { canUpdate } = usePermissions();
  const [tabAtiva, setTabAtiva] = useState<TabAtiva>('nao_conformidades');

  // Labels dinâmicos de tipo de ocorrência (carregados do backend)
  const [ocorrenciaTipoLabel, setOcorrenciaTipoLabel] = useState<Record<string, string>>(OCORRENCIA_TIPO_LABEL_FALLBACK);

  // Modal de justificativa
  const [modalJustificativa, setModalJustificativa] = useState<{
    isOpen: boolean;
    tipo: 'nc' | 'div';
    id: number;
    titulo: string;
  }>({ isOpen: false, tipo: 'nc', id: 0, titulo: '' });

  useEffect(() => {
    const carregarTipos = async () => {
      try {
        const result = await listarTiposOcorrencia();
        if (result.success && result.tipos.length > 0) {
          const labels: Record<string, string> = {};
          for (const tipo of result.tipos) {
            labels[tipo.codigo] = tipo.nome;
          }
          setOcorrenciaTipoLabel(prev => ({ ...prev, ...labels }));
        }
      } catch {
        // Mantém fallback em caso de erro
      }
    };
    carregarTipos();
  }, []);

  // --- Não Conformidades state ---
  const [naoConformidades, setNaoConformidades] = useState<NaoConformidade[]>([]);
  const [estatisticas, setEstatisticas] = useState<NaoConformidadeEstatisticas | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [numeroPedidoFilter, setNumeroPedidoFilter] = useState('');
  const [dataInicio, setDataInicio] = useState<string>(getHojeISO());
  const [dataFim, setDataFim] = useState<string>(getHojeISO());
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // --- Divergência Ocorrências state ---
  const [divergencias, setDivergencias] = useState<DivergenciaOcorrencia[]>([]);
  const [divEstatisticas, setDivEstatisticas] = useState<DivergenciaOcorrenciaEstatisticas | null>(null);
  const [divLoading, setDivLoading] = useState(false);
  const [divFilter, setDivFilter] = useState('');
  const [divStatusFilter, setDivStatusFilter] = useState('');
  const [divNumeroPedidoFilter, setDivNumeroPedidoFilter] = useState('');
  const [divDataInicio, setDivDataInicio] = useState<string>(getHojeISO());
  const [divDataFim, setDivDataFim] = useState<string>(getHojeISO());
  const [divShowFilters, setDivShowFilters] = useState(false);
  const [divPagination, setDivPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [divExpandedId, setDivExpandedId] = useState<number | null>(null);

  // --- Não Conformidades data fetching ---
  const fetchData = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      // Busca por identificador (filter texto ou numero_pedido) desconsidera
      // filtro de data e pesquisa todo historico.
      const numeroPedidoTrim = numeroPedidoFilter.trim();
      const buscaAtiva = Boolean((filter && filter.trim()) || numeroPedidoTrim);
      const dataInicioParam = buscaAtiva ? undefined : toInicioDiaISO(dataInicio);
      const dataFimParam = buscaAtiva ? undefined : toFimDiaISO(dataFim);
      const [result, statsResult] = await Promise.all([
        listarNaoConformidades({
          page,
          limit: 20,
          busca: filter || undefined,
          status: statusFilter || undefined,
          tipo: tipoFilter || undefined,
          numero_pedido: numeroPedidoTrim || undefined,
          data_inicio: dataInicioParam,
          data_fim: dataFimParam
        }),
        page === 1
          ? obterEstatisticasNaoConformidades({ data_inicio: dataInicioParam, data_fim: dataFimParam })
          : Promise.resolve(null)
      ]);

      if (result.success) {
        setNaoConformidades(result.naoConformidades);
        setPagination(result.pagination);
      }

      if (statsResult && statsResult.success) {
        setEstatisticas(statsResult.data);
      }
    } catch (error: any) {
      console.error('Erro ao carregar não conformidades:', error);
      utils.showNotification(error.message || 'Erro ao carregar não conformidades', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [filter, statusFilter, tipoFilter, numeroPedidoFilter, dataInicio, dataFim]);

  // --- Divergência Ocorrências data fetching ---
  const fetchDivergencias = useCallback(async (page = 1) => {
    setDivLoading(true);
    try {
      // Busca por identificador (divFilter texto ou numero_pedido) desconsidera
      // filtro de data e pesquisa todo historico.
      const numeroPedidoTrim = divNumeroPedidoFilter.trim();
      const buscaAtiva = Boolean((divFilter && divFilter.trim()) || numeroPedidoTrim);
      const dataInicioParam = buscaAtiva ? undefined : toInicioDiaISO(divDataInicio);
      const dataFimParam = buscaAtiva ? undefined : toFimDiaISO(divDataFim);
      const [result, statsResult] = await Promise.all([
        listarDivergenciaOcorrencias({
          page,
          limit: 20,
          busca: divFilter || undefined,
          status: divStatusFilter || undefined,
          numero_pedido: numeroPedidoTrim || undefined,
          data_inicio: dataInicioParam,
          data_fim: dataFimParam
        }),
        page === 1
          ? obterEstatisticasDivergenciaOcorrencias({ data_inicio: dataInicioParam, data_fim: dataFimParam })
          : Promise.resolve(null)
      ]);

      if (result.success) {
        setDivergencias(result.ocorrencias);
        setDivPagination(result.pagination);
      }

      if (statsResult && statsResult.success) {
        setDivEstatisticas(statsResult.data);
      }
    } catch (error: any) {
      console.error('Erro ao carregar ocorrências de divergência:', error);
      utils.showNotification(error.message || 'Erro ao carregar ocorrências', { type: 'error' });
    } finally {
      setDivLoading(false);
    }
  }, [divFilter, divStatusFilter, divNumeroPedidoFilter, divDataInicio, divDataFim]);

  // Carregar estatísticas de divergências (para os cards do topo), respeitando filtro de data
  // Quando ha busca ativa, desconsidera o filtro de data para consistencia com a lista.
  useEffect(() => {
    const buscaAtiva = Boolean((divFilter && divFilter.trim()) || divNumeroPedidoFilter.trim());
    obterEstatisticasDivergenciaOcorrencias({
      data_inicio: buscaAtiva ? undefined : toInicioDiaISO(divDataInicio),
      data_fim: buscaAtiva ? undefined : toFimDiaISO(divDataFim)
    }).then(res => {
      if (res.success) setDivEstatisticas(res.data);
    }).catch(() => {});
  }, [divDataInicio, divDataFim, divFilter, divNumeroPedidoFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (tabAtiva === 'divergencias') {
      fetchDivergencias();
    }
  }, [tabAtiva, fetchDivergencias]);

  const handleStatusChange = async (id: number, newStatus: string) => {
    if (newStatus === 'resolvido') {
      const nc = naoConformidades.find(n => n.id === id);
      setModalJustificativa({
        isOpen: true,
        tipo: 'nc',
        id,
        titulo: nc ? `NC: ${tipoLabel[nc.tipo] || nc.tipo}` : 'Não conformidade',
      });
      return;
    }

    try {
      await atualizarNaoConformidade(id, { status: newStatus });
      utils.showNotification('Status atualizado com sucesso!', { type: 'success' });
      fetchData(pagination.page);
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao atualizar status', { type: 'error' });
    }
  };

  const handleDivStatusChange = async (id: number, newStatus: string) => {
    if (newStatus === 'resolvido') {
      const div = divergencias.find(d => d.id === id);
      setModalJustificativa({
        isOpen: true,
        tipo: 'div',
        id,
        titulo: div ? `Pedido: ${div.numero_pedido} - ${ocorrenciaTipoLabel[div.ocorrencia_tipo] || div.ocorrencia_tipo}` : 'Ocorrência de divergência',
      });
      return;
    }

    try {
      await atualizarDivergenciaOcorrencia(id, { status: newStatus });
      utils.showNotification('Status da ocorrência atualizado com sucesso!', { type: 'success' });
      fetchDivergencias(divPagination.page);
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao atualizar status', { type: 'error' });
    }
  };

  const handleConfirmResolucao = async (justificativa: string) => {
    const { tipo, id } = modalJustificativa;
    setModalJustificativa(prev => ({ ...prev, isOpen: false }));

    try {
      if (tipo === 'nc') {
        await atualizarNaoConformidade(id, { status: 'resolvido', observacoes: justificativa });
        utils.showNotification('Não conformidade resolvida com sucesso!', { type: 'success' });
        fetchData(pagination.page);
      } else {
        await atualizarDivergenciaOcorrencia(id, { status: 'resolvido', observacoes: justificativa });
        utils.showNotification('Ocorrência resolvida com sucesso!', { type: 'success' });
        fetchDivergencias(divPagination.page);
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao resolver', { type: 'error' });
    }
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const parseVolumes = (val: any): number[] => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try { return JSON.parse(val); } catch { return []; }
    }
    return [];
  };

  // Calcular estatísticas de ocorrências por categoria
  const getOcorrenciasPorCategoria = () => {
    if (!divEstatisticas?.por_tipo) return null;

    let erroConferente = { total: 0, pendentes: 0, analisando: 0, resolvidos: 0 };
    let duplicataCliente = { total: 0, pendentes: 0, analisando: 0, resolvidos: 0 };
    let demais = { total: 0, pendentes: 0, analisando: 0, resolvidos: 0 };

    for (const t of divEstatisticas.por_tipo) {
      if (t.tipo === 'duplicata_erro_conferente') {
        erroConferente = { total: t.total, pendentes: t.pendentes, analisando: t.analisando, resolvidos: t.resolvidos };
      } else if (t.tipo === 'duplicata_cliente_enviou') {
        duplicataCliente = { total: t.total, pendentes: t.pendentes, analisando: t.analisando, resolvidos: t.resolvidos };
      } else {
        demais.total += t.total;
        demais.pendentes += t.pendentes;
        demais.analisando += t.analisando;
        demais.resolvidos += t.resolvidos;
      }
    }

    return { erroConferente, duplicataCliente, demais };
  };

  if (isLoading && naoConformidades.length === 0) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1><i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i> Não Conformidades</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando não conformidades...</p>
        </div>
      </div>
    );
  }

  const ocorrenciasCategoria = getOcorrenciasPorCategoria();

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i> Não Conformidades</h1>
      </div>

      {/* Cards Analíticos - Não Conformidades */}
      {estatisticas && (
        <div className="montagem-stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-exclamation-triangle" style={{ color: '#8e44ad' }}></i>
              <span>Total NC</span>
            </div>
            <p className="stat-value" style={{ color: '#8e44ad' }}>{estatisticas.total}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-clock" style={{ color: '#e67e22' }}></i>
              <span>NC Pendentes</span>
            </div>
            <p className="stat-value" style={{ color: '#e67e22' }}>{estatisticas.por_status.pendentes}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-search" style={{ color: '#3498db' }}></i>
              <span>NC Analisando</span>
            </div>
            <p className="stat-value" style={{ color: '#3498db' }}>{estatisticas.por_status.analisando}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-check-circle" style={{ color: '#27ae60' }}></i>
              <span>NC Resolvidas</span>
            </div>
            <p className="stat-value" style={{ color: '#27ae60' }}>{estatisticas.por_status.resolvidos}</p>
          </div>
        </div>
      )}

      {/* Cards Analíticos - Ocorrências de Divergência */}
      {divEstatisticas && (
        <div className="montagem-stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-balance-scale" style={{ color: '#8e44ad' }}></i>
              <span>Total Ocorrências</span>
            </div>
            <p className="stat-value" style={{ color: '#8e44ad' }}>{divEstatisticas.total}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-clock" style={{ color: '#e67e22' }}></i>
              <span>Ocor. Pendentes</span>
            </div>
            <p className="stat-value" style={{ color: '#e67e22' }}>{divEstatisticas.pendentes}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-search" style={{ color: '#3498db' }}></i>
              <span>Ocor. Analisando</span>
            </div>
            <p className="stat-value" style={{ color: '#3498db' }}>{divEstatisticas.analisando}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-check-circle" style={{ color: '#27ae60' }}></i>
              <span>Ocor. Resolvidas</span>
            </div>
            <p className="stat-value" style={{ color: '#27ae60' }}>{divEstatisticas.resolvidos}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-box-open" style={{ color: '#e74c3c' }}></i>
              <span>Vol. Faltantes</span>
            </div>
            <p className="stat-value" style={{ color: '#e74c3c' }}>{divEstatisticas.total_volumes_faltantes}</p>
          </div>
        </div>
      )}

      {/* Cards por tipo de ocorrência */}
      {ocorrenciasCategoria && (
        <div className="montagem-stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <div className="montagem-stat-card" style={{ borderLeft: '4px solid #e74c3c' }}>
            <div className="stat-header">
              <i className="fas fa-user-times" style={{ color: '#e74c3c' }}></i>
              <span>Duplicata - Erro Conferente</span>
            </div>
            <p className="stat-value" style={{ color: '#e74c3c' }}>{ocorrenciasCategoria.erroConferente.total}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: '0.7rem' }}>
              <span style={{ color: '#e67e22' }}>{ocorrenciasCategoria.erroConferente.pendentes} pend.</span>
              <span style={{ color: '#3498db' }}>{ocorrenciasCategoria.erroConferente.analisando} anal.</span>
              <span style={{ color: '#27ae60' }}>{ocorrenciasCategoria.erroConferente.resolvidos} res.</span>
            </div>
          </div>
          <div className="montagem-stat-card" style={{ borderLeft: '4px solid #e67e22' }}>
            <div className="stat-header">
              <i className="fas fa-clone" style={{ color: '#e67e22' }}></i>
              <span>Duplicata - Cliente Enviou</span>
            </div>
            <p className="stat-value" style={{ color: '#e67e22' }}>{ocorrenciasCategoria.duplicataCliente.total}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: '0.7rem' }}>
              <span style={{ color: '#e67e22' }}>{ocorrenciasCategoria.duplicataCliente.pendentes} pend.</span>
              <span style={{ color: '#3498db' }}>{ocorrenciasCategoria.duplicataCliente.analisando} anal.</span>
              <span style={{ color: '#27ae60' }}>{ocorrenciasCategoria.duplicataCliente.resolvidos} res.</span>
            </div>
          </div>
          <div className="montagem-stat-card" style={{ borderLeft: '4px solid #3498db' }}>
            <div className="stat-header">
              <i className="fas fa-list-alt" style={{ color: '#3498db' }}></i>
              <span>Demais Ocorrências</span>
            </div>
            <p className="stat-value" style={{ color: '#3498db' }}>{ocorrenciasCategoria.demais.total}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: '0.7rem' }}>
              <span style={{ color: '#e67e22' }}>{ocorrenciasCategoria.demais.pendentes} pend.</span>
              <span style={{ color: '#3498db' }}>{ocorrenciasCategoria.demais.analisando} anal.</span>
              <span style={{ color: '#27ae60' }}>{ocorrenciasCategoria.demais.resolvidos} res.</span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #ecf0f1', marginBottom: 0 }}>
        <button
          onClick={() => setTabAtiva('nao_conformidades')}
          style={{
            flex: 1,
            padding: '12px 20px',
            border: 'none',
            borderBottom: tabAtiva === 'nao_conformidades' ? '3px solid #e74c3c' : '3px solid transparent',
            background: tabAtiva === 'nao_conformidades' ? '#fff' : 'transparent',
            color: tabAtiva === 'nao_conformidades' ? '#e74c3c' : '#7f8c8d',
            fontWeight: tabAtiva === 'nao_conformidades' ? 600 : 400,
            cursor: 'pointer',
            fontSize: '0.95rem',
            transition: 'all 0.2s',
            marginBottom: '-2px',
          }}
        >
          <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
          Não Conformidades
          {estatisticas && estatisticas.por_status.pendentes > 0 && (
            <span style={{
              marginLeft: 8,
              background: '#e74c3c',
              color: '#fff',
              borderRadius: 12,
              padding: '2px 8px',
              fontSize: '0.75rem',
            }}>
              {estatisticas.por_status.pendentes}
            </span>
          )}
        </button>
        <button
          onClick={() => setTabAtiva('divergencias')}
          style={{
            flex: 1,
            padding: '12px 20px',
            border: 'none',
            borderBottom: tabAtiva === 'divergencias' ? '3px solid #e67e22' : '3px solid transparent',
            background: tabAtiva === 'divergencias' ? '#fff' : 'transparent',
            color: tabAtiva === 'divergencias' ? '#e67e22' : '#7f8c8d',
            fontWeight: tabAtiva === 'divergencias' ? 600 : 400,
            cursor: 'pointer',
            fontSize: '0.95rem',
            transition: 'all 0.2s',
            marginBottom: '-2px',
          }}
        >
          <i className="fas fa-balance-scale" style={{ marginRight: 8 }}></i>
          Ocorrências de Divergência
          {divEstatisticas && divEstatisticas.pendentes > 0 && (
            <span style={{
              marginLeft: 8,
              background: '#e67e22',
              color: '#fff',
              borderRadius: 12,
              padding: '2px 8px',
              fontSize: '0.75rem',
            }}>
              {divEstatisticas.pendentes}
            </span>
          )}
        </button>
      </div>

      <div className="page-content">
        {/* ============================
            Tab: Não Conformidades
            ============================ */}
        {tabAtiva === 'nao_conformidades' && (
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Registros de Não Conformidades</h2>
              <FilterPanel
                fields={[
                  {
                    type: 'date',
                    label: 'Data Inicial',
                    value: dataInicio,
                    onChange: setDataInicio
                  },
                  {
                    type: 'date',
                    label: 'Data Final',
                    value: dataFim,
                    onChange: setDataFim
                  },
                  {
                    type: 'text',
                    label: 'Buscar',
                    value: filter,
                    onChange: setFilter,
                    placeholder: 'Descrição, código...'
                  },
                  {
                    type: 'text',
                    label: 'Nº do Pedido',
                    value: numeroPedidoFilter,
                    onChange: setNumeroPedidoFilter,
                    placeholder: 'Filtrar por número do pedido...'
                  },
                  {
                    type: 'select',
                    label: 'Status',
                    value: statusFilter,
                    onChange: setStatusFilter,
                    options: [
                      { value: '', label: 'Todos' },
                      { value: 'pendente', label: 'Pendente' },
                      { value: 'analisando', label: 'Analisando' },
                      { value: 'resolvido', label: 'Resolvido' }
                    ]
                  },
                  {
                    type: 'select',
                    label: 'Tipo',
                    value: tipoFilter,
                    onChange: setTipoFilter,
                    options: [
                      { value: '', label: 'Todos' },
                      { value: 'etiqueta_nao_reconhecida', label: 'Etiqueta não reconhecida' },
                      { value: 'palete_nao_identificado', label: 'Palete não identificado' },
                      { value: 'area_nao_encontrada', label: 'Área não encontrada' },
                      { value: 'erro_registro', label: 'Erro ao registrar' },
                      { value: 'etiqueta_sem_pre_carregamento', label: 'Sem pré-carregamento' },
                      { value: 'erro_identificacao_palete', label: 'Erro identificação palete' }
                    ]
                  }
                ]}
                onFilter={() => fetchData(1)}
                onClear={() => {
                  setFilter('');
                  setStatusFilter('');
                  setTipoFilter('');
                  setNumeroPedidoFilter('');
                  setDataInicio(getHojeISO());
                  setDataFim(getHojeISO());
                }}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                title="Filtros"
              />
            </div>

            <div className="card-body">
              {naoConformidades.length > 0 && (
                <ResultsCounter
                  displayedItems={naoConformidades.length}
                  totalItems={pagination.total}
                  isLoading={isLoading}
                  label="registros"
                />
              )}

              {naoConformidades.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-check-circle empty-state-icon" style={{ color: '#27ae60' }}></i>
                  <h3>Nenhuma não conformidade encontrada</h3>
                  <p>{filter || statusFilter || tipoFilter ? 'Tente ajustar os filtros de busca.' : 'Nenhum problema foi registrado no descarregamento.'}</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th>Severidade</th>
                        <th>Conferente</th>
                        <th>Filial</th>
                        <th>Cliente</th>
                        <th>Código Lido</th>
                        <th>Palete</th>
                        <th>Status</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {naoConformidades.map(nc => (
                        <React.Fragment key={nc.id}>
                          <tr
                            style={{ cursor: 'pointer' }}
                            onClick={() => setExpandedId(expandedId === nc.id ? null : nc.id)}
                          >
                            <td>{formatDate(nc.criado_em)}</td>
                            <td>
                              <span style={{ fontSize: '0.85rem' }}>
                                {tipoLabel[nc.tipo] || nc.tipo}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${severidadeClass[nc.severidade] || ''}`}>
                                {severidadeLabel[nc.severidade] || nc.severidade}
                              </span>
                            </td>
                            <td>{nc.conferente_nome || '-'}</td>
                            <td>{nc.conferente_filial || '-'}</td>
                            <td>{nc.cliente_razao_social || '-'}</td>
                            <td>
                              <code style={{ fontSize: '0.8rem', background: '#f0f0f0', padding: '2px 6px', borderRadius: 4 }}>
                                {nc.codigo_lido ? (nc.codigo_lido.length > 30 ? nc.codigo_lido.substring(0, 30) + '...' : nc.codigo_lido) : '-'}
                              </code>
                            </td>
                            <td>{nc.numero_palete ? `#${nc.numero_palete}` : '-'}</td>
                            <td>
                              <span className={`status-badge ${statusClass[nc.status] || ''}`}>
                                {statusLabel[nc.status] || nc.status}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                                {canUpdate(SystemModule.CONFERENCIA_CARGA) && nc.status !== 'resolvido' && (
                                  <>
                                    {nc.status === 'pendente' && (
                                      <button
                                        className="btn-edit"
                                        onClick={() => handleStatusChange(nc.id, 'analisando')}
                                        title="Marcar como analisando"
                                      >
                                        <i className="fas fa-search"></i>
                                      </button>
                                    )}
                                    <button
                                      className="btn-edit"
                                      onClick={() => handleStatusChange(nc.id, 'resolvido')}
                                      title="Marcar como resolvido"
                                      style={{ color: '#fff' }}
                                    >
                                      <i className="fas fa-check"></i>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          {expandedId === nc.id && (
                            <tr>
                              <td colSpan={10} style={{ background: '#f8f9fa', padding: '16px 20px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.9rem' }}>
                                  <div>
                                    <strong>Descrição:</strong>
                                    <p style={{ margin: '4px 0', color: '#555' }}>{nc.descricao}</p>
                                  </div>
                                  {nc.codigo_lido && (
                                    <div>
                                      <strong>Código completo:</strong>
                                      <p style={{ margin: '4px 0', fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>{nc.codigo_lido}</p>
                                    </div>
                                  )}
                                  {nc.dados_adicionais && Object.keys(nc.dados_adicionais).length > 0 && (
                                    <div style={{ gridColumn: '1 / -1' }}>
                                      <strong>Dados adicionais:</strong>
                                      <pre style={{ margin: '4px 0', fontSize: '0.8rem', background: '#fff', padding: '8px', borderRadius: 4, overflow: 'auto' }}>
                                        {JSON.stringify(nc.dados_adicionais, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                  {nc.resolvido_por_nome && (
                                    <div>
                                      <strong>Resolvido por:</strong> {nc.resolvido_por_nome}
                                      {nc.resolvido_em && <span style={{ color: '#7f8c8d' }}> em {formatDate(nc.resolvido_em)}</span>}
                                    </div>
                                  )}
                                  {nc.observacoes && (
                                    <div style={{ gridColumn: '1 / -1' }}>
                                      <strong>Justificativa da resolução:</strong>
                                      <p style={{ margin: '4px 0', color: '#555', padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #eee' }}>{nc.observacoes}</p>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>

                  {/* Paginação */}
                  {pagination.totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
                      <button
                        className="btn btn-secondary"
                        disabled={!pagination.page || pagination.page <= 1}
                        onClick={() => fetchData(pagination.page - 1)}
                      >
                        Anterior
                      </button>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px', color: '#7f8c8d' }}>
                        Página {pagination.page} de {pagination.totalPages}
                      </span>
                      <button
                        className="btn btn-secondary"
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() => fetchData(pagination.page + 1)}
                      >
                        Próxima
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================
            Tab: Ocorrências de Divergência
            ============================ */}
        {tabAtiva === 'divergencias' && (
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Ocorrências de Divergência</h2>
              <FilterPanel
                fields={[
                  {
                    type: 'date',
                    label: 'Data Inicial',
                    value: divDataInicio,
                    onChange: setDivDataInicio
                  },
                  {
                    type: 'date',
                    label: 'Data Final',
                    value: divDataFim,
                    onChange: setDivDataFim
                  },
                  {
                    type: 'text',
                    label: 'Buscar',
                    value: divFilter,
                    onChange: setDivFilter,
                    placeholder: 'Pedido, justificativa...'
                  },
                  {
                    type: 'text',
                    label: 'Nº do Pedido',
                    value: divNumeroPedidoFilter,
                    onChange: setDivNumeroPedidoFilter,
                    placeholder: 'Filtrar por número do pedido...'
                  },
                  {
                    type: 'select',
                    label: 'Status',
                    value: divStatusFilter,
                    onChange: setDivStatusFilter,
                    options: [
                      { value: '', label: 'Todos' },
                      { value: 'pendente', label: 'Pendente' },
                      { value: 'analisando', label: 'Analisando' },
                      { value: 'resolvido', label: 'Resolvido' }
                    ]
                  }
                ]}
                onFilter={() => fetchDivergencias(1)}
                onClear={() => {
                  setDivFilter('');
                  setDivStatusFilter('');
                  setDivNumeroPedidoFilter('');
                  setDivDataInicio(getHojeISO());
                  setDivDataFim(getHojeISO());
                }}
                showFilters={divShowFilters}
                onToggleFilters={() => setDivShowFilters(!divShowFilters)}
                title="Filtros"
              />
            </div>

            <div className="card-body">
              {divergencias.length > 0 && (
                <ResultsCounter
                  displayedItems={divergencias.length}
                  totalItems={divPagination.total}
                  isLoading={divLoading}
                  label="ocorrências"
                />
              )}

              {divLoading && divergencias.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <div className="loading-spinner-large"></div>
                  <p>Carregando ocorrências de divergência...</p>
                </div>
              ) : divergencias.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-check-circle empty-state-icon" style={{ color: '#27ae60' }}></i>
                  <h3>Nenhuma ocorrência de divergência</h3>
                  <p>{divFilter || divStatusFilter ? 'Tente ajustar os filtros de busca.' : 'Nenhuma divergência de volumes foi registrada.'}</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Pedido</th>
                        <th>Tipo</th>
                        <th>Cliente</th>
                        <th>Volumes</th>
                        <th>Justificativa</th>
                        <th>Status</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {divergencias.map(div => {
                        const isExpanded = divExpandedId === div.id;
                        const volumesConf = parseVolumes(div.volumes_conferidos);
                        const volumesFalt = parseVolumes(div.volumes_faltantes);

                        return (
                          <React.Fragment key={div.id}>
                            <tr
                              style={{ cursor: 'pointer' }}
                              onClick={() => setDivExpandedId(isExpanded ? null : div.id)}
                            >
                              <td>{formatDate(div.criado_em)}</td>
                              <td>
                                <strong style={{ color: '#2c3e50' }}>{div.numero_pedido}</strong>
                              </td>
                              <td>
                                <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: '0.85rem' }}>
                                    {ocorrenciaTipoLabel[div.ocorrencia_tipo] || div.ocorrencia_tipo}
                                  </span>
                                  {div.is_ponto_corte && (
                                    <span style={{
                                      fontSize: '0.7rem', fontWeight: 600,
                                      padding: '2px 6px', borderRadius: 8,
                                      background: '#fff3e0', color: '#e65100',
                                      whiteSpace: 'nowrap',
                                    }}>
                                      <i className="fas fa-cut" style={{ marginRight: 3 }}></i>
                                      Corte
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>{div.cliente_razao_social || '-'}</td>
                              <td>
                                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    background: '#ebf5fb',
                                    color: '#2980b9',
                                    borderRadius: 4,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                  }}>
                                    {div.total_volumes_conferido}/{div.total_volumes_esperado}
                                  </span>
                                  {div.total_volumes_faltante > 0 && (
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      background: '#ffebee',
                                      color: '#e74c3c',
                                      borderRadius: 4,
                                      fontSize: '0.8rem',
                                      fontWeight: 600,
                                    }}>
                                      -{div.total_volumes_faltante}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span style={{ fontSize: '0.85rem', color: '#555' }}>
                                  {div.justificativa
                                    ? (div.justificativa.length > 40 ? div.justificativa.substring(0, 40) + '...' : div.justificativa)
                                    : <em style={{ color: '#bbb' }}>Sem justificativa</em>
                                  }
                                </span>
                              </td>
                              <td>
                                <span className={`status-badge ${statusClass[div.status] || ''}`}>
                                  {statusLabel[div.status] || div.status}
                                </span>
                              </td>
                              <td>
                                <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                                  {canUpdate(SystemModule.CONFERENCIA_DIVERGENCIAS) && div.status !== 'resolvido' && (
                                    <>
                                      {div.status === 'pendente' && (
                                        <button
                                          className="btn-edit"
                                          onClick={() => handleDivStatusChange(div.id, 'analisando')}
                                          title="Marcar como analisando"
                                        >
                                          <i className="fas fa-search"></i>
                                        </button>
                                      )}
                                      <button
                                        className="btn-edit"
                                        onClick={() => handleDivStatusChange(div.id, 'resolvido')}
                                        title="Marcar como resolvido"
                                        style={{ color: '#fff' }}
                                      >
                                        <i className="fas fa-check"></i>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={8} style={{ background: '#f8f9fa', padding: '16px 20px' }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '0.9rem' }}>
                                    {/* Volumes info */}
                                    <div>
                                      <strong>Volumes conferidos ({volumesConf.length}):</strong>
                                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                                        {volumesConf.length > 0 ? volumesConf.map(v => (
                                          <span key={v} style={{
                                            display: 'inline-flex', alignItems: 'center',
                                            padding: '3px 10px', background: '#ebf5fb', color: '#2980b9',
                                            borderRadius: 16, fontSize: '0.85rem', fontWeight: 600,
                                          }}>
                                            <i className="fas fa-check" style={{ marginRight: 4, fontSize: '0.7rem' }}></i>
                                            Vol. {v}
                                          </span>
                                        )) : <span style={{ color: '#bbb' }}>Nenhum</span>}
                                      </div>
                                    </div>
                                    <div>
                                      <strong style={{ color: '#e74c3c' }}>Volumes faltantes ({volumesFalt.length}):</strong>
                                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                                        {volumesFalt.length > 0 ? volumesFalt.map(v => (
                                          <span key={v} style={{
                                            display: 'inline-flex', alignItems: 'center',
                                            padding: '3px 10px', background: '#ffebee', color: '#e74c3c',
                                            borderRadius: 16, fontSize: '0.85rem', fontWeight: 600,
                                          }}>
                                            <i className="fas fa-times" style={{ marginRight: 4, fontSize: '0.7rem' }}></i>
                                            Vol. {v}
                                          </span>
                                        )) : <span style={{ color: '#bbb' }}>Nenhum</span>}
                                      </div>
                                    </div>

                                    {/* Indicador de ponto de corte */}
                                    {div.is_ponto_corte && (
                                      <div style={{
                                        gridColumn: '1 / -1',
                                        padding: '10px 14px', background: '#fff3e0', borderRadius: 8,
                                        border: '1px solid #ffe0b2', display: 'flex', alignItems: 'center', gap: 10,
                                      }}>
                                        <i className="fas fa-cut" style={{ color: '#e65100', fontSize: '1rem' }}></i>
                                        <div>
                                          <strong style={{ color: '#e65100', fontSize: '0.85rem' }}>Ponto de Corte</strong>
                                          <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#bf360c' }}>
                                            O pedido foi dividido entre múltiplos pallets. As etiquetas bipadas permaneceram no pallet
                                            e as restantes foram geradas em novos pallets.
                                          </p>
                                        </div>
                                      </div>
                                    )}

                                    {/* Justificativa */}
                                    {div.justificativa && (
                                      <div style={{ gridColumn: '1 / -1' }}>
                                        <strong>Justificativa:</strong>
                                        <p style={{ margin: '4px 0', color: '#555', padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #eee' }}>
                                          {div.justificativa}
                                        </p>
                                      </div>
                                    )}

                                    {/* Etiquetas */}
                                    {div.etiquetas && div.etiquetas.length > 0 && (
                                      <div style={{ gridColumn: '1 / -1' }}>
                                        <strong>
                                          <i className="fas fa-barcode" style={{ marginRight: 6 }}></i>
                                          Etiquetas ({div.etiquetas.length})
                                        </strong>
                                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                          {div.etiquetas.map((etiq, idx) => (
                                            <div
                                              key={idx}
                                              style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '8px 12px',
                                                background: etiq.status === 'faltante' ? '#ffebee' : '#ebf5fb',
                                                borderRadius: 6,
                                                border: `1px solid ${etiq.status === 'faltante' ? '#ffcdd2' : '#bee5eb'}`,
                                              }}
                                            >
                                              <div style={{ flex: 1 }}>
                                                <code style={{
                                                  fontFamily: 'monospace',
                                                  fontSize: '0.85rem',
                                                  wordBreak: 'break-all',
                                                  color: etiq.status === 'faltante' ? '#e74c3c' : '#2c3e50',
                                                }}>
                                                  {etiq.codigo_lido}
                                                </code>
                                              </div>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 12, whiteSpace: 'nowrap' }}>
                                                {etiq.volume_atual && etiq.total_volumes && (
                                                  <span style={{
                                                    fontSize: '0.8rem',
                                                    color: etiq.status === 'faltante' ? '#e74c3c' : '#2980b9',
                                                    fontWeight: 600,
                                                  }}>
                                                    Vol. {etiq.volume_atual}/{etiq.total_volumes}
                                                  </span>
                                                )}
                                                <span style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  padding: '2px 8px',
                                                  borderRadius: 12,
                                                  fontSize: '0.75rem',
                                                  fontWeight: 600,
                                                  background: etiq.status === 'faltante' ? '#e74c3c' : '#3498db',
                                                  color: '#fff',
                                                }}>
                                                  {etiq.status === 'faltante' ? (
                                                    <><i className="fas fa-times" style={{ marginRight: 4, fontSize: '0.65rem' }}></i>Faltante</>
                                                  ) : (
                                                    <><i className="fas fa-check" style={{ marginRight: 4, fontSize: '0.65rem' }}></i>Conferido</>
                                                  )}
                                                </span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Registrado por / Resolvido por */}
                                    <div>
                                      {div.registrado_por_nome && (
                                        <p><strong>Registrado por:</strong> {div.registrado_por_nome}</p>
                                      )}
                                    </div>
                                    <div>
                                      {div.resolvido_por_nome && (
                                        <p>
                                          <strong>Resolvido por:</strong> {div.resolvido_por_nome}
                                          {div.resolvido_em && <span style={{ color: '#7f8c8d' }}> em {formatDate(div.resolvido_em)}</span>}
                                        </p>
                                      )}
                                    </div>

                                    {div.observacoes && (
                                      <div style={{ gridColumn: '1 / -1' }}>
                                        <strong>Justificativa da resolução:</strong>
                                        <p style={{ margin: '4px 0', color: '#555', padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #eee' }}>{div.observacoes}</p>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Paginação */}
                  {divPagination.totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
                      <button
                        className="btn btn-secondary"
                        disabled={!divPagination.page || divPagination.page <= 1}
                        onClick={() => fetchDivergencias(divPagination.page - 1)}
                      >
                        Anterior
                      </button>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px', color: '#7f8c8d' }}>
                        Página {divPagination.page} de {divPagination.totalPages}
                      </span>
                      <button
                        className="btn btn-secondary"
                        disabled={divPagination.page >= divPagination.totalPages}
                        onClick={() => fetchDivergencias(divPagination.page + 1)}
                      >
                        Próxima
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de justificativa para resolução */}
      <JustificativaModal
        isOpen={modalJustificativa.isOpen}
        titulo={modalJustificativa.titulo}
        onConfirm={handleConfirmResolucao}
        onCancel={() => setModalJustificativa(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default NaoConformidadesList;
